/**
 * @module experience/browse-plans
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ /** */
// Interfaces
import {
  FamilyPlans,
  Family,
  PlanOption,
  Line,
  Category,
} from '@support/api/proxies/responses/api-2117.interface';
import {
  FamilyPlan,
  FamilyPlanResponse,
  AllPlans,
  GrandfatheredCategory,
} from '@experience/shop/softgoods/interfaces/family-plan.interface';

// Constants
import { SERVICES_AND_PLANS } from '@experience/shop/shared-browse/constants/shared-browse.constants';
// import {CHANNELS, SETTINGS} from '@environment/settings';
import { FLOW } from '@experience/shared-experience/shared-experience.constants';

// Services
import { CommonUtilService } from '@support/shared/services/common-util.service';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';
import {
  Cart,
  CartLine,
  CartItem,
} from '@support/api/proxies/responses/api-cart-response.interface';

export class BrowsePlansResponseModel {
  public selectedLineId: string;
  public getCart: Cart;
  public getLineId: string;
  public familyId: string;
  public selectedPlan: PlanOption;
  private familyPlan: FamilyPlans;
  private commonUtil: CommonUtilService;
  private objectService: ObjectService;
  private allPlans: AllPlans;
  private selectedFamilyCode: string;
  constructor(
    private response: FamilyPlans,
    private cart: Cart,
    private line: string
  ) {
    this.familyPlan = this.response;
    this.getCart = this.cart;
    this.getLineId = this.line;
    this.commonUtil = new CommonUtilService();
    this.objectService = new ObjectService();
  }

  /**
   * Function sets familyId,familyName, familyPlans
   * @returns array of family plans
   */
  public processFamilyResponse(
    selectedLine: number,
    flow?: string,
    lineId?: string,
    selectedPlanId?: string
  ): FamilyPlanResponse {
    const familyPlans = [];
    this.allPlans = this.getAllPlans();
    const grandFatherCategory: GrandfatheredCategory = <GrandfatheredCategory>new Object({});
    const nonGrandFatheredPlans = this.allPlans.nonGrandFathered;
    if (this.objectService.get(nonGrandFatheredPlans, 'family')) {
      for (const item in <Object>nonGrandFatheredPlans.family) {
        if (nonGrandFatheredPlans.family[item]) {
          const itemValue = nonGrandFatheredPlans.family[item];
          const familyPlanObject: FamilyPlan = <FamilyPlan>new Object({});
          // set familyId
          familyPlanObject.familyCode = this.getFamilyId(nonGrandFatheredPlans, item);
          familyPlanObject.familyId = this.getFamilyCode(nonGrandFatheredPlans, item);
          // set familyName
          familyPlanObject.familyName = this.getFamilyName(nonGrandFatheredPlans, item);
          // set description
          familyPlanObject.description = this.getFamilyDesc(nonGrandFatheredPlans, item);
          if (itemValue.plans && itemValue.plans.planConfigurationId) {
            familyPlanObject.planConfigurationId = itemValue.plans.planConfigurationId;
          }
          // get all family plans
          familyPlanObject.planOptions = this.getFamilyPlans(
            itemValue,
            selectedLine,
            false,
            flow,
            lineId
          );
          familyPlans.push(familyPlanObject);
        }
      }
    }
    if (this.allPlans.grandFathered) {
      grandFatherCategory.categoryName = this.allPlans.grandFathered.categoryName;
      grandFatherCategory.family = this.getGrandFatheredFamilyPlans(
        this.allPlans.grandFathered,
        selectedLine,
        flow,
        lineId
      );
    }

    const result: FamilyPlanResponse = {
      familyPlans: familyPlans,
      selectedLineId: this.selectedLineId,
      selectedPlan: this.selectedPlan ? this.selectedPlan : null,
      selectedPlanFamily: this.selectedPlan ? this.selectedFamilyCode : '',
      grandFatheredPlans: grandFatherCategory,
    };
    return result;
  }

  /**
   * Method to set the plan when we edit from cart
   * @returns void
   */
  public setSelectedPlan(
    lineItem: Line,
    flow: string,
    isGrandfathered: boolean,
    familyCode: string
  ): void {
    const cart = this.getCart;
    let selectedPlanId: string;
    if (
      (flow === SERVICES_AND_PLANS.CPS || flow === FLOW.CONFLICT) &&
      cart &&
      this.objectService.get(cart, 'lines')
    ) {
      const currentLine = cart.lines.find((line: CartLine) => line.id === this.getLineId);
      if (currentLine) {
        const selectedPlan = currentLine.plan.find((plan: CartItem) => {
          return plan.statusCode === SERVICES_AND_PLANS.ADD;
        });
        selectedPlanId = this.objectService.get(selectedPlan, 'offerId');
      }
    }
    if (selectedPlanId) {
      this.selectedPlan = lineItem.planOptions.find((option: PlanOption) => {
        return option.planOptionSelectorId === selectedPlanId;
      });
      this.selectedFamilyCode = isGrandfathered ? SERVICES_AND_PLANS.GRANDFATHERED : familyCode;
    } else if (flow !== FLOW.CONFLICT) {
      this.selectedPlan = lineItem.planOptions.find((option: PlanOption) => {
        return option.isActive;
      });
      this.selectedFamilyCode = isGrandfathered ? SERVICES_AND_PLANS.GRANDFATHERED : familyCode;
    }
  }

  /**
   * This method filters out all the Grandfathered plans for Unassisted flows except for active plan
   * @param data : Grandfathered plans that needs to be filtered out
   * @returns FamilyPlan[]
   */
  public getValidGrandfatheredPlans(data: FamilyPlan[]): FamilyPlan[] {
    // const isAssistedFlow = (SETTINGS.CHANNEL.toLowerCase() === CHANNELS.RETAIL ||
    // SETTINGS.CHANNEL.toLowerCase() === CHANNELS.CARE);
    // if (isAssistedFlow) {
    //   return data;
    // }
    // else {
    // For WEB channel, only display the currently active Grandfathered plan
    if (this.selectedFamilyCode === SERVICES_AND_PLANS.GRANDFATHERED) {
      const grandfatheredPlans: FamilyPlan[] = [];
      data.forEach((currentFamily: FamilyPlan) => {
        const selectedPlan = currentFamily.planOptions.find(
          (plan: PlanOption) =>
            plan.planOptionSelectorId ===
            this.objectService.get(this.selectedPlan, 'planOptionSelectorId')
        );
        if (selectedPlan) {
          let family: FamilyPlan = <FamilyPlan>new Object();
          family = currentFamily;
          family.planOptions = [];
          family.planOptions.push(this.selectedPlan);
          grandfatheredPlans.push(family);
        }
      });
      return grandfatheredPlans;
    } else {
      return [];
    }
    // }
  }

  /**
   * Function gets familyName
   * @param index of type string
   * @returns array of familyNames
   */
  private getFamilyName(category: Category, index: string): string {
    const familyName = this.objectService.get(category, `family.${index}.familyName`);
    return familyName ? familyName : '';
  }

  /**
   * Function gets family Description
   * @param index of type string
   * @returns array of familyDescription
   */
  private getFamilyDesc(category: Category, index: string): string {
    const familyDesc = this.objectService.get(category, `family.${index}.familyDescription`);
    return familyDesc ? familyDesc : '';
  }

  /**
   * Function gets familyId
   * @param index of type string
   * @returns familyId
   */
  private getFamilyId(category: Category, index: string): string {
    const familyId = this.objectService.get(category, `family.${index}.familyCode`);
    return familyId ? familyId : '';
  }

  /**
   * Function gets familyId
   * @param index of type string
   * @returns familyId
   */
  private getFamilyCode(category: Category, index: string): string {
    const familyId = this.objectService.get(category, `family.${index}.familyId`);
    return familyId ? familyId : '';
  }

  /**
   * Function gets familyPlans
   * @param index of type string
   * @returns array of familyPlans
   */
  private getFamilyPlans(
    itemValue: Family,
    selectedLine: number,
    isGrandfathered: boolean,
    flow?: string,
    lineId?: string
  ): PlanOption[] {
    let lineItem: Line = <Line>new Object();
    const lines = this.objectService.get(itemValue, 'plans.lines');
    const isArray = Array.isArray(lines);
    if (flow === SERVICES_AND_PLANS.CPS || flow === FLOW.CONFLICT) {
      if (lines && isArray) {
        lineItem = itemValue.plans.lines.filter((lineItems: Line) => {
          return lineItems.lineId === lineId;
        })[0];
      }
    } else {
      if (lines && isArray && itemValue.plans.lines.length >= selectedLine) {
        lineItem = itemValue.plans.lines.filter((lineItems: Line) => {
          return lineItems.lineNumber === selectedLine;
        })[0];
      }
    }
    return this.setSelectedId(lineItem, flow, isGrandfathered, itemValue);
  }

  /**
   * Method to set selected plan Id
   * @param lineItem
   * @param flow
   * @param isGrandfathered
   * @param itemValue
   */
  private setSelectedId(
    lineItem: Line,
    flow: string,
    isGrandfathered: boolean,
    itemValue: Family
  ): PlanOption[] {
    let matchingResults = [];
    lineItem = itemValue.plans.lines[0];
    if (lineItem && lineItem.planOptions) {
      matchingResults = lineItem.planOptions;
      if (!this.objectService.get(this.selectedPlan, 'planOptionSelectorId')) {
        this.setSelectedPlan(lineItem, flow, isGrandfathered, itemValue.familyCode);
      }
    }
    this.selectedLineId = lineItem.lineId;
    return matchingResults;
  }

  /**
   * Function gets grandfather family Plans
   * @param itemValue Family structure to get list of grandfather plans
   * @param lineId Line id of current line
   * @returns array of familyPlans
   */
  private getGrandFatheredFamilyPlans(
    itemValue: Category,
    selectedLineNumber: number,
    flow?: string,
    lineId?: string
  ): FamilyPlan[] {
    const grandFatherPlans = [];
    itemValue.family.forEach((family: Family, index: number) => {
      if (
        family.plans &&
        family.plans.lines &&
        Array.isArray(family.plans.lines) &&
        family.plans.lines.length
      ) {
        const grandFatherPlansObject: FamilyPlan = <FamilyPlan>new Object({});
        grandFatherPlansObject.description = this.getFamilyDesc(itemValue, index.toString());
        grandFatherPlansObject.familyName = this.getFamilyName(itemValue, index.toString());
        grandFatherPlansObject.familyId = this.getFamilyCode(itemValue, index.toString());
        grandFatherPlansObject.planOptions = this.getFamilyPlans(
          family,
          selectedLineNumber,
          true,
          flow,
          lineId
        );
        grandFatherPlans.push(grandFatherPlansObject);
      }
    });
    return this.getValidGrandfatheredPlans(grandFatherPlans);
  }

  /**
   * Function to get all the plans (including Grandfathered) from the response
   * @returns AllPlans
   */
  private getAllPlans(): AllPlans {
    const sepratedPlans: AllPlans = <AllPlans>new Object({});
    if (this.objectService.get(this.familyPlan, 'category')) {
      this.familyPlan.category.forEach((category: Category) => {
        if (category.categoryId === SERVICES_AND_PLANS.GRANDFATHERED) {
          sepratedPlans.grandFathered = category;
        } else {
          sepratedPlans.nonGrandFathered = category;
        }
      });
    }
    return sepratedPlans;
  }
}
